import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { toast } from "sonner";
import {
  Factory, Plus, Leaf, Euro as EuroIcon, MapPin, Droplets, Zap, Edit2, Trash2,
  Loader2, ArrowUpRight,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = [
  { value: "en_despliegue",  label: "En despliegue",  color: "bg-blue-100 text-blue-800" },
  { value: "en_explotacion", label: "En explotación", color: "bg-emerald-100 text-emerald-800" },
  { value: "mantenimiento",  label: "Mantenimiento",  color: "bg-amber-100 text-amber-800" },
  { value: "retirado",       label: "Retirado",       color: "bg-slate-200 text-slate-600" },
];

const emptyForm = {
  name: "", description: "", initiative_id: "", pilot_id: "",
  fecha_go_live: "", contrato_marco: "", responsable_operativo: "",
  estado_operativo: "en_despliegue",
  sitios_str: "",
  presupuesto_2026: "", presupuesto_2027: "", presupuesto_2028: "",
  co2_tons: "", eur_savings: "", agua_m3: "", energia_kwh: "",
  notes: "",
};

function toForm(p) {
  return {
    ...emptyForm,
    id: p.id,
    name: p.name || "", description: p.description || "",
    initiative_id: p.initiative_id || "", pilot_id: p.pilot_id || "",
    fecha_go_live: p.fecha_go_live ? p.fecha_go_live.substring(0, 10) : "",
    contrato_marco: p.contrato_marco || "", responsable_operativo: p.responsable_operativo || "",
    estado_operativo: p.estado_operativo || "en_despliegue",
    sitios_str: (p.sitios_desplegados || []).join(", "),
    presupuesto_2026: p.presupuesto_anual?.["2026"] || "",
    presupuesto_2027: p.presupuesto_anual?.["2027"] || "",
    presupuesto_2028: p.presupuesto_anual?.["2028"] || "",
    co2_tons:    p.kpis_impacto?.co2_tons || "",
    eur_savings: p.kpis_impacto?.eur_savings || "",
    agua_m3:     p.kpis_impacto?.agua_m3 || "",
    energia_kwh: p.kpis_impacto?.energia_kwh || "",
    notes: p.notes || "",
  };
}

function fromForm(f) {
  const num = (v) => (v === "" || v === null ? undefined : Number(v));
  const presupuesto_anual = {};
  if (f.presupuesto_2026) presupuesto_anual["2026"] = num(f.presupuesto_2026);
  if (f.presupuesto_2027) presupuesto_anual["2027"] = num(f.presupuesto_2027);
  if (f.presupuesto_2028) presupuesto_anual["2028"] = num(f.presupuesto_2028);
  const kpis_impacto = {};
  if (f.co2_tons)    kpis_impacto.co2_tons    = num(f.co2_tons);
  if (f.eur_savings) kpis_impacto.eur_savings = num(f.eur_savings);
  if (f.agua_m3)     kpis_impacto.agua_m3     = num(f.agua_m3);
  if (f.energia_kwh) kpis_impacto.energia_kwh = num(f.energia_kwh);
  return {
    name: f.name, description: f.description || null,
    initiative_id: f.initiative_id || null, pilot_id: f.pilot_id || null,
    fecha_go_live: f.fecha_go_live || null,
    contrato_marco: f.contrato_marco || null,
    responsable_operativo: f.responsable_operativo || null,
    estado_operativo: f.estado_operativo,
    sitios_desplegados: f.sitios_str.split(",").map(s => s.trim()).filter(Boolean),
    presupuesto_anual, kpis_impacto,
    notes: f.notes || null,
  };
}

export default function ProductionPage() {
  const { token, isAdmin } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [productions, setProductions] = useState([]);
  const [stats, setStats] = useState(null);
  const [initiatives, setInitiatives] = useState([]);
  const [pendingScaled, setPendingScaled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, sRes, iRes, plRes, pAllRes] = await Promise.all([
        axios.get(`${API}/productions`, auth),
        axios.get(`${API}/productions/stats`, auth),
        axios.get(`${API}/initiatives`, auth),
        axios.get(`${API}/pilots`, auth),
        axios.get(`${API}/productions?include_demo=true`, auth),
      ]);
      setProductions(pRes.data);
      setStats(sRes.data);
      setInitiatives(iRes.data);
      // Fix 2026-08-27: usar include_demo=true para el diagnóstico de "escalados sin
      // ficha" — si no, los pilotos de las 2 fichas demo (ya con producción real, solo
      // ocultas de la lista) aparecían como "sin ficha" y el botón de promover no hacía
      // nada visible (ya estaban promovidos).
      const linkedPilotIds = new Set(pAllRes.data.map(p => p.pilot_id).filter(Boolean));
      setPendingScaled((plRes.data || []).filter(p => p.decision === "scale" && !linkedPilotIds.has(p.id)));
    } catch {
      toast.error("Error cargando producciones");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const promoteFromPilot = async (pilotId) => {
    setPromoting(pilotId);
    try {
      await axios.post(`${API}/productions/from-pilot/${pilotId}`, {}, auth);
      toast.success("Ficha de producción creada");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear la ficha");
    } finally { setPromoting(null); }
  };

  const openCreate = () => { setForm(emptyForm); setEditing(null); setDialogOpen(true); };
  const openEdit = (p) => { setForm(toForm(p)); setEditing(p.id); setDialogOpen(true); };

  const submit = async () => {
    if (!form.name?.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = fromForm(form);
      if (editing) {
        await axios.put(`${API}/productions/${editing}`, payload, auth);
        toast.success("Producción actualizada");
      } else {
        await axios.post(`${API}/productions`, payload, auth);
        toast.success("Producción creada");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const remove = (id, name) => setDeleteTarget({ id, name });

  const confirmRemove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/productions/${deleteTarget.id}`, auth);
      toast.success("Eliminada");
      await load();
    } catch { toast.error("Error"); }
    finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const formatEur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="space-y-6 animate-in" data-testid="production-page">
      {/* Hero */}
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
        <CardContent className="p-6 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-2">
              <Factory className="w-4 h-4" /> Producción / Escalado
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Pilotos escalados en operación</h1>
            <p className="text-sm text-slate-600 mt-1">
              Soluciones ya validadas que han pasado a producción. Trazabilidad completa iniciativa → piloto → producción.
            </p>
          </div>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-production-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva producción
          </Button>
        </CardContent>
      </Card>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total" value={stats.total} icon={Factory} />
          <StatCard label="CO₂ ahorrado" value={`${stats.total_co2_tons.toLocaleString("es-ES")} t`} icon={Leaf} color="text-emerald-600" />
          <StatCard label="€ ahorrados / año" value={formatEur(stats.total_eur_savings)} icon={EuroIcon} color="text-blue-600" />
          <StatCard label="Sitios desplegados" value={stats.total_sites} icon={MapPin} color="text-purple-600" />
          <StatCard label="En explotación" value={stats.by_state.en_explotacion} icon={Factory} color="text-emerald-700" />
        </div>
      )}

      {/* Diagnóstico: pilotos escalados sin ficha de producción (G12) */}
      {pendingScaled.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60 dark:bg-amber-950/20" data-testid="pending-scaled-pilots-banner">
          <CardContent className="py-4">
            <p className="font-medium text-amber-800 dark:text-amber-400 mb-2">
              Hay {pendingScaled.length} piloto{pendingScaled.length > 1 ? "s" : ""} escalado{pendingScaled.length > 1 ? "s" : ""} sin ficha de producción
            </p>
            <div className="space-y-2">
              {pendingScaled.map(p => (
                <div key={p.id} className="flex items-center justify-between gap-3 bg-background rounded-lg px-3 py-2" data-testid={`pending-scaled-${p.id}`}>
                  <span className="text-sm truncate">{p.name}</span>
                  <Button size="sm" variant="outline" onClick={() => promoteFromPilot(p.id)} disabled={promoting === p.id} data-testid={`promote-pilot-${p.id}`}>
                    {promoting === p.id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" />}
                    Crear ficha de producción
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Cargando…</div>
      ) : productions.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12 text-slate-500">
            <Factory className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aún no hay producciones. Escala tu primer piloto desde la ficha del piloto o crea una manualmente.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {productions.map(p => {
            const st = STATES.find(s => s.value === p.estado_operativo) || STATES[0];
            const budget = Object.values(p.presupuesto_anual || {}).reduce((a, b) => a + Number(b || 0), 0);
            return (
              <Card key={p.id} className="hover:shadow-md transition-shadow" data-testid={`production-card-${p.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${st.color} hover:opacity-90`}>{st.label}</Badge>
                        {p.contrato_marco && <span className="text-xs text-slate-500">CM: {p.contrato_marco}</span>}
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 truncate">{p.name}</h3>
                      <div className="text-xs text-slate-500 mt-1">
                        {p.initiative_name && <span>Iniciativa: <strong>{p.initiative_name}</strong></span>}
                        {p.pilot_name && <span> · desde piloto <strong>{p.pilot_name}</strong></span>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-production-${p.id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => remove(p.id, p.name)} className="text-red-500" data-testid={`delete-production-${p.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {p.description && <p className="text-sm text-slate-600 mb-3 line-clamp-2">{p.description}</p>}

                  {p.sitios_desplegados?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {p.sitios_desplegados.slice(0, 6).map(s => (
                        <span key={s} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full">
                          <MapPin className="w-3 h-3" /> {s}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    {p.kpis_impacto?.co2_tons ? <KPI icon={Leaf} label="CO₂/año" value={`${p.kpis_impacto.co2_tons} t`} color="text-emerald-600" /> : null}
                    {p.kpis_impacto?.eur_savings ? <KPI icon={EuroIcon} label="Ahorro" value={formatEur(p.kpis_impacto.eur_savings)} color="text-blue-600" /> : null}
                    {p.kpis_impacto?.agua_m3 ? <KPI icon={Droplets} label="Agua" value={`${p.kpis_impacto.agua_m3} m³`} color="text-cyan-600" /> : null}
                    {p.kpis_impacto?.energia_kwh ? <KPI icon={Zap} label="Energía" value={`${p.kpis_impacto.energia_kwh} kWh`} color="text-amber-600" /> : null}
                  </div>

                  {!(p.kpis_impacto?.co2_tons) && !(p.kpis_impacto?.eur_savings) && (
                    <button
                      onClick={() => openEdit(p)}
                      className="mt-2 w-full text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 rounded-md py-1.5 hover:bg-amber-100 transition-colors"
                      data-testid={`production-fill-kpis-${p.id}`}
                    >
                      Sin KPIs de impacto registrados (CO₂/ahorro/agua/energía) — clic para rellenar
                    </button>
                  )}

                  {budget > 0 && (
                    <div className="mt-3 text-xs text-slate-500">
                      Presupuesto plurianual: <strong className="text-slate-700">{formatEur(budget)}</strong>
                      {p.responsable_operativo && <span> · Responsable: <strong>{p.responsable_operativo}</strong></span>}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar producción" : "Nueva producción"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="p-name">Nombre *</Label>
              <Input id="p-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej: Bioestabilizado en Getafe · escalado" data-testid="p-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="p-desc">Descripción</Label>
              <Textarea id="p-desc" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Iniciativa vinculada</Label>
                <Select value={form.initiative_id} onValueChange={v => setForm({ ...form, initiative_id: v })}>
                  <SelectTrigger><SelectValue placeholder="(opcional)" /></SelectTrigger>
                  <SelectContent>
                    {initiatives.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estado operativo</Label>
                <Select value={form.estado_operativo} onValueChange={v => setForm({ ...form, estado_operativo: v })}>
                  <SelectTrigger data-testid="p-state-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Fecha go-live</Label>
                <Input type="date" value={form.fecha_go_live} onChange={e => setForm({ ...form, fecha_go_live: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Contrato marco</Label>
                <Input value={form.contrato_marco} onChange={e => setForm({ ...form, contrato_marco: e.target.value })} placeholder="Ej: CM-2026-EX-01" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sitios desplegados (separados por coma)</Label>
              <Input value={form.sitios_str} onChange={e => setForm({ ...form, sitios_str: e.target.value })} placeholder="Planta Getafe, Planta Alcalá, Estación Torrejón" />
            </div>
            <div className="space-y-2">
              <Label>Responsable operativo</Label>
              <Input value={form.responsable_operativo} onChange={e => setForm({ ...form, responsable_operativo: e.target.value })} placeholder="Nombre y apellidos" />
            </div>

            <div className="border-t pt-3">
              <div className="text-sm font-semibold text-slate-700 mb-2">Presupuesto anual (€)</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label className="text-xs">2026</Label><Input type="number" value={form.presupuesto_2026} onChange={e => setForm({ ...form, presupuesto_2026: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">2027</Label><Input type="number" value={form.presupuesto_2027} onChange={e => setForm({ ...form, presupuesto_2027: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">2028</Label><Input type="number" value={form.presupuesto_2028} onChange={e => setForm({ ...form, presupuesto_2028: e.target.value })} /></div>
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="text-sm font-semibold text-slate-700 mb-2">KPIs de impacto (anuales)</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">CO₂ ahorrado (t)</Label><Input type="number" value={form.co2_tons} onChange={e => setForm({ ...form, co2_tons: e.target.value })} data-testid="p-kpi-co2-input" /></div>
                <div className="space-y-1"><Label className="text-xs">€ ahorrados</Label><Input type="number" value={form.eur_savings} onChange={e => setForm({ ...form, eur_savings: e.target.value })} data-testid="p-kpi-savings-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Agua (m³)</Label><Input type="number" value={form.agua_m3} onChange={e => setForm({ ...form, agua_m3: e.target.value })} data-testid="p-kpi-agua-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Energía (kWh)</Label><Input type="number" value={form.energia_kwh} onChange={e => setForm({ ...form, energia_kwh: e.target.value })} data-testid="p-kpi-energia-input" /></div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-production-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editing ? "Guardar cambios" : "Crear"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Eliminar producción"
        description={`¿Estás seguro de que quieres eliminar "${deleteTarget?.name}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={confirmRemove}
        loading={deleting}
        testId="delete-production-confirm-dialog"
      />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = "text-slate-900" }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 mb-1">
          {Icon && <Icon className="w-3.5 h-3.5" />}{label}
        </div>
        <div className={`text-xl font-bold ${color}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className={`w-3.5 h-3.5 ${color}`} />
      <div>
        <div className="text-[10px] uppercase text-slate-500">{label}</div>
        <div className={`font-semibold ${color}`}>{value}</div>
      </div>
    </div>
  );
}
